import { useMutation } from '@tanstack/react-query';
import { strToU8, zipSync } from 'fflate';
import type { ExportFilter } from '@casetrace/export';
import { CaseIdSchema } from '@casetrace/shared';
import { exportTimeline, type ExportTimelineResult } from '../../lib/callables';

type ExportTimelineMutationInput = {
  readonly caseId: string;
  readonly saksnummer: string;
  readonly filter: ExportFilter;
};

function sanitizeFilePart(value: string): string {
  return value.trim().replace(/[^a-zA-Z0-9._-]+/g, '_') || 'sak';
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function downloadZip(result: ExportTimelineResult, saksnummer: string): void {
  const zipInput: Record<string, Uint8Array> = {};
  for (const file of result.files) {
    zipInput[file.name] = strToU8(file.content);
  }

  const blob = new Blob([zipSync(zipInput)], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `sakspakke_${sanitizeFilePart(saksnummer)}_${todayIsoDate()}.zip`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function useExportTimeline() {
  return useMutation({
    mutationFn: async (input: ExportTimelineMutationInput) => {
      const result = await exportTimeline({
        caseId: CaseIdSchema.parse(input.caseId),
        filter: input.filter,
      });
      downloadZip(result, input.saksnummer);
      return result;
    },
  });
}
